import React, { useCallback, useContext, useLayoutEffect, useRef } from 'react'
import ScrollerContext from 'components/Scroller/Context'
import { css } from '@emotion/react'

const style = css`
  position: fixed;
  z-index: 10;
  border-radius: 10px;
`

const FixedAIOverlayContainer = ({
  topRef,
  bottomRef,
  widthRef,
  bottomAnchor = 'top',
  children,
}) => {
  const fixedRef = useRef(null)
  const rafIdRef = useRef(null)
  const { getScrollerAPI } = useContext(ScrollerContext)

  const updateFixedContainer = useCallback(
    () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
      }

      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null
        if (!topRef.current || !bottomRef.current || !widthRef.current) {
          return
        }
        const topRect = topRef.current.getBoundingClientRect()
        const bottomRect = bottomRef.current.getBoundingClientRect()
        const containerRect = widthRef.current.getBoundingClientRect()
        const topOffset = topRect.top + 10
        let bottomOffset
        if (bottomAnchor === 'top') {
          bottomOffset = bottomRect.top
        } else if (bottomAnchor === 'bottom') {
          bottomOffset = bottomRect.bottom - 1
        } else {
          throw new Error('invalid bottomAnchor prop')
        }
        const height = bottomOffset - topOffset
        const leftOffset = containerRect.left + 1
        const rightOffset = containerRect.right - 1
        const width = rightOffset - leftOffset

        if (height > 0 && fixedRef.current) {
          fixedRef.current.style.top = `${topOffset}px`
          fixedRef.current.style.height = `${height}px`
          fixedRef.current.style.left = `${leftOffset}px`
          fixedRef.current.style.width = `${width}px`
        }
      })
    },
    [rafIdRef, topRef, bottomRef, widthRef, bottomAnchor]
  )

  useLayoutEffect(
    () => {
      updateFixedContainer()
      let scrollEl = getScrollerAPI()?.getElement()
      if (scrollEl) {
        scrollEl.addEventListener('scroll', updateFixedContainer)
      }

      return () => {
        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current)
        }
        scrollEl = getScrollerAPI()?.getElement()
        if (scrollEl) {
          scrollEl.removeEventListener('scroll', updateFixedContainer)
        }
      }
    },
    [
      topRef,
      bottomRef,
      widthRef,
      rafIdRef,
      updateFixedContainer,
      getScrollerAPI,
    ]
  )

  return (
    <div ref={fixedRef} css={style}>
      {children}
    </div>
  )
}

export default FixedAIOverlayContainer
